import {
  Compiler,
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Injectable,
  Input,
  NgModule,
  NgModuleRef,
  Output,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';

import * as ts from "typescript";
import {DynamicComponentOptions} from "./dynamic-component.options";
import {PortalBaseComponent} from "../../components/portal-base/portal-base.component";

import {PortalService} from "../portal/portal.service";
import {ThemeService} from "../theme/theme.service";
import {FeatureFlagsService} from "../feature-flag/feature-flags.service";
import {CmdbService} from "../cmdb/cmdb.service";
import {KnockoutFieldComponent} from "../../components/knockout-field/knockout-field.component";
import {FieldService} from '../field/field.service';

// This is a structural copy of the Component directive with unnecessary properties removed. It replaces
// the Component directive if one is supplied in the user defined Angular component. This is because @Component triggers
// compilation of the component, and we can only compile once if "optimization: true" in angular.json (because some
// internal properties have "configurable: !!ngDevMode" and ngDevMode is undefined if "optimization: true")
declare interface DynamicComponentConfig extends Directive {
  template?: string;
  styles?: string[];
}

function DynamicComponent(config: DynamicComponentConfig) {
  return function (target: any) {
    // Do nothing as the presence of the directive is sufficient to get metadata emitted
  }
}

interface ComponentCacheEntry {
  concatenatedParts?: string;
  componentFactory?: ComponentFactory<any>;
  moduleRef?: NgModuleRef<any>;
  compilationPromise?: Promise<ComponentRef<any>>;
}

@Injectable({
  providedIn: 'root'
})
export class DynamicComponentService {

  private componentCache: {
    [key: string]: ComponentCacheEntry
  } = {};

  private readonly CLASSNAME = '__className';
  private readonly COMPONENT_DECORATOR = '__componentDecorator';

  constructor(
    private compiler: Compiler,
    private componentFactoryResolver: ComponentFactoryResolver,
    private themeService: ThemeService,
    private featureFlagsService: FeatureFlagsService,
    private fieldService: FieldService
  ) { }

  async makeDynamicComponent(options: DynamicComponentOptions) {

    let { type, id, content, injector, modules, template, styles, implementation, parameters, data, formioComponent, dataFunction } = options;

    // Concatenated parts is used to detect if a component definition has changed or not. If it has not changed we pull the
    // component factory from a cache. Id is the servicely record that contains the dynamic component definition.
    const concatenatedParts = implementation + template + styles;

    const componentCacheEntry = this.componentCache[id];

    if (componentCacheEntry && componentCacheEntry.concatenatedParts === concatenatedParts) {
      // Component definition has not changed. Reusing the existing one.
      return componentCacheEntry.compilationPromise.then( () => { return this.createComponent(options, componentCacheEntry) });
    } else {
      this.componentCache[id] = {
        concatenatedParts: concatenatedParts,
        compilationPromise: new Promise<ComponentRef<any>>((resolve, reject) => {
          this.compileComponent(options).then((cacheEntry => {
            if (this.componentCache[id].moduleRef) {
              this.componentCache[id].moduleRef.destroy();
            }

            this.componentCache[id].moduleRef = cacheEntry.moduleRef;
            this.componentCache[id].componentFactory = cacheEntry.componentFactory;
            resolve(null);
          })).catch((e) => {
            console.error("DynamicComponentService.makeDynamicComponent failed compile component definition for " + id, e);
          });
        })
      };

      return this.componentCache[id].compilationPromise.then( () => { return this.createComponent(options, this.componentCache[id])});
    }
  }

  async compileComponent(options: DynamicComponentOptions) {

    let { type, id, content, injector, modules, template, styles, implementation, parameters, data, formioComponent, dataFunction } = options;

    // If the implementation is really just a template then this is the default className.
    const defaultClassName = 'DefaultDynamicComponent_' + id;

    // If the implementation is really just a template then this is the default implementation.
    const defaultImplementations = {
      search:
        `@DynamicComponent({template: ''})
        class ${defaultClassName} {
          constructor(private hostElement: ElementRef) {
          }
      }`,
      cmdb:
        `@DynamicComponent({template: ''})
        class ${defaultClassName} {
          constructor(private hostElement: ElementRef) {
          }
      }`,
      portal:
        `@DynamicComponent({template: ''})
         class ${defaultClassName} extends PortalBaseComponent {
           constructor(private portalService: PortalService, private hostElement: ElementRef) {
             super(portalService, hostElement);
           }
          ngOnInit() {
            super.ngOnInit();
          }
          ngOnDestroy() {
            super.ngOnDestroy();
          }
        }`
    };

    const defaultImplementation = defaultImplementations[type];

    // If the implementation is really just a template then this is the default styles.
    const defaultStyles = [];

    // Substitute @DynamicComponent for @Component if present
    implementation = implementation.replace("@Component", "@DynamicComponent");

    const componentProperties = this.getComponentProperties(implementation);

    const implementationClassName  = componentProperties[this.CLASSNAME] || defaultClassName;

    // The string passed in as 'implementation' may be a just a template or a full component definition,
    // componentProperties tells us which.
    template = componentProperties?.template || template || implementation;
    styles = componentProperties?.styles || styles || defaultStyles;
    implementation = componentProperties[this.CLASSNAME] ? implementation : defaultImplementation;

    if (type === 'portal') {
      // Portal components need to extend from PortalBaseComponent
      if (!implementation.match(/extends\s+PortalBaseComponent/)) {
        implementation = implementation.replace(implementationClassName, ` ${implementationClassName} extends PortalBaseComponent `);
      }
      // Portal components need a decorator for metadata to be emitted
      const componentProperties = this.getComponentProperties(implementation);
      if (!componentProperties[this.COMPONENT_DECORATOR]) {
        const addedDecorator = `@DynamicComponent({template: ''})`;
        implementation = addedDecorator + ' ' + implementation;
      }
    }

    if (type === 'portal' && componentProperties[this.CLASSNAME]) {
      // Portal components need to have a two arg constructor
      implementation = implementation.replace("constructor", "__constructor__");
      implementation = implementation.replace("ngOnInit", "__ngOnInit__");
      implementation = implementation.replace("ngOnDestroy", "__ngOnDestroy__");

      const addedMethods = `
      constructor(public portalService: PortalService, public hostElement: ElementRef) {
        super(portalService, hostElement);
        this.__constructor__ && this.__constructor__();
      }

      ngOnInit() {
        super.ngOnInit();
        this.__ngOnInit__ && this.__ngOnInit__();
      }

      ngOnDestroy() {
        super.ngOnDestroy();
        this.__ngOnDestroy__ && this.__ngOnDestroy__();
      }
      `;

      // Find last '}' and insert before there
      const idx = implementation.lastIndexOf('}');
      implementation = implementation.substr(0, idx) + ' ' + addedMethods + ' ' + implementation.substr(idx);
    }

    const transpiledImplementation = ts.transpile(implementation, { target: ts.ScriptTarget.ES2015, emitDecoratorMetadata: true });

    // Set base component for dynamic components. Without declaring the PortalBaseComponent as
    // global it is not seen when eval'ing the code.
    // We add the other decorators so that we can include them in FullTextSearch templates
    // and thus have template, styles and implementation all together.
    (window as any).PortalBaseComponent = PortalBaseComponent;
    (window as any).Component = Component;
    (window as any).Input = Input;
    (window as any).Output = Output;
    (window as any).ViewChild = ViewChild;
    (window as any).ViewChildren = ViewChildren;
    (window as any).ElementRef = ElementRef;
    (window as any).PortalService = PortalService;
    (window as any).CmdbService = CmdbService;
    (window as any).ViewEncapsulation = ViewEncapsulation;

    (window as any).KnockoutFieldComponent = KnockoutFieldComponent;
    (window as any).FieldService = this.fieldService;

    // TODO: other services that you want to access by constructor injection in dynamic components need to be mentioned
    // here as well. The right way is to bundle them up as has been done with PortalService and CmdbService
    // (window as any).UserService = UserService;

    // If we did this with 'new Function(...)" instead of "eval" we might not need to put the stuff above onto window.
    const componentImpl = eval(`${transpiledImplementation}; ${implementationClassName};`);

    // Template and styles might already be declared in the implementation. This can happen for components
    // such as infoboxes on CMDB or Search.
    template = componentProperties['template'] ?? template;
    styles = componentProperties['styles'] ?? styles;

    const component = Component(
      {
        template: template,
        styles: Array.isArray(styles) ? styles : [styles],
        inputs: [],
        outputs: [],
        providers: [],
        viewProviders: []
      })(componentImpl);

    // Three ways to get the component factory - not sure which is better. No. 3 might not make a dynamic module.

    // 1. Define a dynamic module containing this component
    const module = NgModule(
      {
        declarations: [component],
        imports: modules,
        providers: []
      })(class {});

    this.compiler.clearCache(); // TODO is this needed?

    const compiledModule = await this.compiler.compileModuleAndAllComponentsAsync(module);

    const moduleRef = compiledModule.ngModuleFactory.create(injector);

    // 1.1. Use the compiled module to get the component factory directly
    // const componentFactory = compiledModule.componentFactories[0];

    // 1.2. Use the compiled module to create a module ref and use that to resolve the component factory
    const componentFactory = moduleRef.componentFactoryResolver.resolveComponentFactory(component);

    // 3. use the AppModule component factory resolver for the component
    // const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component) as ComponentFactory<any>;

    return {
      componentFactory: componentFactory,
      moduleRef: moduleRef
    } as ComponentCacheEntry;
  }

  private createComponent(options: DynamicComponentOptions, componentCacheEntry: ComponentCacheEntry): ComponentRef<any> {
    let { type, id, content, injector, modules, template, styles, implementation, parameters, data, formioComponent, dataFunction } = options;

    const componentRef = content.createComponent(componentCacheEntry.componentFactory, 0, injector) as ComponentRef<any>;

    // Make the data part of the component implementation so they can be used in templates.
    Object.keys(data).forEach((key) => {
      componentRef.instance[key] = data[key];
    });

    // As well just copy to a data field.
    componentRef.instance.data = data;

    // The parameters for configuring this component.
    componentRef.instance.parameters = parameters;

    // The formio component associated with this angular component.
    componentRef.instance.formioComponent = formioComponent;

    // Provide a built in method to allow the component to call the Server Script
    componentRef.instance.getData = dataFunction;

    return componentRef;
  }

  private getComponentProperties(implementation: string): any {
    const sourceFile = ts.createSourceFile("dynamic.ts", implementation, ts.ScriptTarget.ES2017);

    const collectedProperties = {};
    const collectProperties = (node: ts.Node) => {

      // The TypeScript compiler returns true for isClassDeclaration() when the node has no name, so we check for that also.
      if (ts.isClassDeclaration(node) && node.name) {
        collectedProperties[this.CLASSNAME] = node.name.escapedText;
        const decorators = ts.canHaveDecorators(node) ? ts.getDecorators(node) : undefined;
        if (decorators) {
          const decorator = decorators.find((decorator) => {
            const decoratorExpr = decorator.expression;
            return (ts.isCallExpression(decoratorExpr) && ((decoratorExpr.expression as any).escapedText === 'Component' || (decoratorExpr.expression as any).escapedText === 'DynamicComponent'));
          });
          if (decorator) {
            collectedProperties[this.COMPONENT_DECORATOR] = true;
            const properties = (decorator.expression as any).arguments?.[0]?.properties;
            if (properties) {
              properties.forEach((prop) => {
                const name = prop.name.escapedText;
                const initializer = prop.initializer;
                if (initializer.text) {
                  collectedProperties[name] = prop.initializer.text;
                } else if (prop.initializer.elements) {
                  collectedProperties[name] = prop.initializer.elements.map((e) => {
                    return e.text;
                  });
                }
              });
            }
          }
        }
      }

      ts.forEachChild(node, collectProperties);
    };

    collectProperties(sourceFile);

    return collectedProperties;
  }

}
